import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import httpClient from "../../services/httpClient";
import Logger from "../../utils/Logger";
import { useAuth } from "../../AuthContext";
import orangeSortIcon from "../../media/orange-sort-icon.svg";
import "../../styleSheet/main/admin/manageCars/manageCars.scss";

const EditCar = () => {
  const { id } = useParams();
  const [make, setMake] = useState("");
  const [model, setModel] = useState("");
  const [licensePlate, setLicensePlate] = useState("");
  const [errors, setErrors] = useState({});
  const [hover, setHover] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const { userData } = useAuth();

  // Sprawdź czy użytkownik jest adminem
  useEffect(() => {
    if (userData.userRole !== "admin") {
      navigate("/");
    }
  }, [userData, navigate]);

  // Pobierz dane samochodu
  useEffect(() => {
    const fetchCar = async () => {
      try {
        setIsLoading(true);
        const response = await httpClient.get(`/Cars/${id}`, {
          withAuth: true,
        });
        const car = response.data;
        setMake(car.make);
        setModel(car.model);
        setLicensePlate(car.licensePlate);
      } catch (error) {
        Logger.error("Błąd podczas pobierania samochodu:", error);
        setErrors({
          fetch: "Nie udało się pobrać danych samochodu",
        });
      } finally {
        setIsLoading(false);
      }
    };

    if (id) {
      fetchCar();
    }
  }, [id]);

  const handleBackClick = () => {
    navigate("/administrator/samochody");
  };

  const validateForm = () => {
    const newErrors = {};

    if (!make.trim()) {
      newErrors.make = "Proszę podać markę samochodu";
    }

    if (!model.trim()) {
      newErrors.model = "Proszę podać model samochodu";
    }

    if (!licensePlate.trim()) {
      newErrors.licensePlate = "Proszę podać numer rejestracyjny";
    } else if (!/^[A-Z0-9\s-]+$/i.test(licensePlate.trim())) {
      newErrors.licensePlate =
        "Numer rejestracyjny może zawierać tylko litery, cyfry, spacje i myślniki";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const carData = {
        id: id,
        make: make.trim(),
        model: model.trim(),
        licensePlate: licensePlate.trim().toUpperCase(),
      };

      await httpClient.put(`/Cars/${id}`, carData, { withAuth: true });

      // Sukces - przekieruj do listy samochodów
      navigate("/administrator/samochody");
    } catch (error) {
      Logger.error("Błąd podczas aktualizacji samochodu:", error);
      setErrors({
        submit:
          error.response?.data?.message ||
          "Nie udało się zaktualizować samochodu. Spróbuj ponownie.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="manage-cars-container">
        <p>Ładowanie danych samochodu...</p>
      </div>
    );
  }

  if (errors.fetch) {
    return (
      <div className="manage-cars-container">
        <div className="error-message">{errors.fetch}</div>
        <button onClick={handleBackClick}>Powrót do listy</button>
      </div>
    );
  }

  return (
    <div className="manage-cars-container">
      <div className="manage-cars-header">
        <h1>Edytuj samochód</h1>
        <div
          className="orange-sort-icon"
          onClick={handleBackClick}
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
          style={{
            backgroundImage: `url(${orangeSortIcon})`,
            transform: hover ? "rotate(180deg)" : "rotate(90deg)",
            cursor: "pointer",
          }}
        />
      </div>

      <form className="car-form" onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="make">Marka samochodu *</label>
          <input
            type="text"
            id="make"
            value={make}
            onChange={(e) => setMake(e.target.value)}
            placeholder="np. Toyota"
            disabled={isSubmitting}
          />
          {errors.make && <span className="error-message">{errors.make}</span>}
        </div>

        <div className="form-group">
          <label htmlFor="model">Model samochodu *</label>
          <input
            type="text"
            id="model"
            value={model}
            onChange={(e) => setModel(e.target.value)}
            placeholder="np. Corolla"
            disabled={isSubmitting}
          />
          {errors.model && (
            <span className="error-message">{errors.model}</span>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="licensePlate">Numer rejestracyjny *</label>
          <input
            type="text"
            id="licensePlate"
            value={licensePlate}
            onChange={(e) => setLicensePlate(e.target.value)}
            placeholder="np. WA 12345"
            disabled={isSubmitting}
          />
          {errors.licensePlate && (
            <span className="error-message">{errors.licensePlate}</span>
          )}
        </div>

        {errors.submit && (
          <div className="error-message submit-error">{errors.submit}</div>
        )}

        <div className="form-actions">
          <button
            type="submit"
            className="submit-button"
            disabled={isSubmitting}
          >
            {isSubmitting ? "Zapisywanie..." : "Zapisz zmiany"}
          </button>
          <button
            type="button"
            className="cancel-button"
            onClick={handleBackClick}
            disabled={isSubmitting}
          >
            Anuluj
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditCar;
